<template>
  <div class="model-animation row-1">
    <div class="model-animation__wrap">
      <div class="model-animation__container">
        <div ref="mount" class="model-animation__model">
          <div class="text-overlay">
            <div class="left">
              <h2
                v-anim-reveal-text
                class="title-big2 is-medium-bold"
                v-html="$t('common.modelTitle')"
              />
            </div>
            <div class="right">
              <p
                v-anim-reveal-text
                class="text-base4"
                v-html="$t('common.modelText')"
              />
            </div>
          </div>
          <div class="model-animation__model__content">
            <!-- Modello 3D -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const mount = ref(null)
let renderer, scene, camera, model

// Inizializza la scena, la camera e il renderer
const initScene = () => {
  scene = new THREE.Scene()
  camera = new THREE.PerspectiveCamera(
    50,
    window.innerWidth / window.innerHeight,
    0.1,
    1000,
  )
  camera.position.set(0, 7.5, 20)
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.setSize(window.innerWidth, window.innerHeight)
  mount.value.appendChild(renderer.domElement)
}

// Aggiunge le luci alla scena
const addLights = () => {
  scene.add(new THREE.AmbientLight(0xf7f7f7, 1.5))
  const directionalLight = new THREE.DirectionalLight(0xf7f7f7, 1.5)
  directionalLight.position.set(-1, 0, 1)
  directionalLight.castShadow = true
  scene.add(directionalLight)
}

// Carica il modello 3D e lo aggiunge alla scena
const loadModel = () => {
  new GLTFLoader().load(
    '/assets/3D_Home.glb',
    (gltf) => {
      model = gltf.scene
      const position = window.innerWidth < 1000 ? 2 : 15
      model.position.set(0, position, 0)
      const scale = window.innerWidth < 1000 ? 60 : 70
      model.scale.set(scale, scale, scale)
      const middleRotation = window.innerWidth < 1000 ? 16 : 18
      model.rotation.set(0, middleRotation, -Math.PI / 35)
      scene.add(model)
      setupScrollTrigger() // Animazione con ScrollTrigger

      // Refresh ScrollTrigger dopo il caricamento del modello
      ScrollTrigger.refresh()
    },
    undefined,
    (error) => {
      console.error('Error loading model:', error)
    },
  )
}

// Funzione di throttle per limitare la frequenza di chiamata
const throttle = (func, limit) => {
  let inThrottle
  return function () {
    const args = arguments
    const context = this
    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

// Configura l'animazione del modello con ScrollTrigger
const setupScrollTrigger = () => {
  const throttledAnimation = throttle(() => {
    if (window.innerWidth >= 1000) {
      gsap.fromTo(
        model.position,
        {
          y: -8,
        },
        {
          y: 0,
          scrollTrigger: {
            trigger: '.model-animation__model',
            start: 'top 40%',
            end: 'bottom bottom',
            scrub: 1,
          },
        },
      )
    }
    gsap.to(model.rotation, {
      y: 10.5,
      scrollTrigger: {
        trigger: '.model-animation__model',
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
      },
    })
  }, 100)

  throttledAnimation()
}

// Funzione di animazione che aggiorna il renderer
const animate = () => {
  if (!isRendering.value) return
  requestAnimationFrame(animate)
  renderer.render(scene, camera)
}

const isRendering = ref(true)

const cleanupResources = () => {
  if (renderer) {
    renderer.dispose()
  }

  if (scene) {
    scene.clear()
  }

  if (model && model.geometry) {
    model.geometry.dispose()
  }

  if (model && model.material) {
    model.material.dispose()
  }

  ScrollTrigger.killAll()
}

onMounted(() => {
  initScene() // Inizializza la scena
  addLights() // Aggiunge le luci
  loadModel() // Carica il modello 3D
  animate() // Avvia l'animazione

  const observer = new IntersectionObserver(
    ([entry]) => {
      isRendering.value = entry.isIntersecting
      if (entry.isIntersecting) animate()
    },
    { threshold: 0.1 },
  )

  if (mount.value) observer.observe(mount.value)
})

// Eseguito prima che il componente venga smontato
onBeforeUnmount(() => {
  // Pulisce le risorse
  cleanupResources()
})
</script>

<script>
export default {
  name: 'ModelAnimation',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
